/**
 * Internet connection status for the staff dashboard (Moeen, Oct 27 2026:
 * offline designs A, C and D approved).
 *
 * `navigator.onLine` is false only when the computer has no network at all.
 * Wi-Fi with no internet still reports true, so a failed API request also
 * runs `checkConnection`, which asks the dashboard's own host for
 * index.html. Any HTTP answer means the internet works; a failed fetch means
 * it does not.
 */

export const connectionRecheckIntervalMs = 10 * 1000;

const FETCH_FAILURE_PATTERN =
  /failed to fetch|load failed|networkerror|network request failed/i;

/**
 * True when a request failed before any HTTP answer: no network, DNS
 * failure, or a dropped connection. A server error (it has a status code)
 * is not a connection failure.
 */
export const isConnectionFailureError = (error: unknown): boolean =>
  error instanceof TypeError &&
  FETCH_FAILURE_PATTERN.test(String(error.message || "")) &&
  !("statusCode" in error);

export type ConnectionStatusStore = {
  isOffline: () => boolean;
  subscribe: (listener: () => void) => () => void;
  /** Asks the dashboard host. Resolves true when the internet works. */
  checkConnection: () => Promise<boolean>;
};

type ConnectionWindow = Pick<
  Window,
  "addEventListener" | "setInterval" | "clearInterval"
> & { navigator: Pick<Navigator, "onLine"> };

export type CreateConnectionStatusStoreInput = {
  win?: ConnectionWindow;
  fetchImpl?: typeof fetch;
  publicUrl?: string;
  recheckIntervalMs?: number;
};

export const createConnectionStatusStore = ({
  win = window,
  fetchImpl = (...args) => fetch(...args),
  publicUrl = process.env.PUBLIC_URL || "",
  recheckIntervalMs = connectionRecheckIntervalMs,
}: CreateConnectionStatusStoreInput = {}): ConnectionStatusStore => {
  let isOffline = !win.navigator.onLine;
  let recheckTimer: number | null = null;
  const listeners = new Set<() => void>();

  // While offline, check again on a timer: Wi-Fi with no internet sends no
  // "online" event when the internet comes back.
  const syncRecheckTimer = () => {
    if (isOffline && recheckTimer === null) {
      recheckTimer = win.setInterval(() => {
        void checkConnection();
      }, recheckIntervalMs);
    }

    if (!isOffline && recheckTimer !== null) {
      win.clearInterval(recheckTimer);
      recheckTimer = null;
    }
  };

  const setOffline = (nextIsOffline: boolean) => {
    if (nextIsOffline === isOffline) {
      return;
    }

    isOffline = nextIsOffline;
    syncRecheckTimer();
    listeners.forEach((listener) => listener());
  };

  const checkConnection = async (): Promise<boolean> => {
    if (!win.navigator.onLine) {
      setOffline(true);
      return false;
    }

    try {
      await fetchImpl(`${publicUrl}/index.html?connectionCheck=${Date.now()}`, {
        method: "HEAD",
        cache: "no-store",
        credentials: "same-origin",
      });
      setOffline(false);
      return true;
    } catch {
      setOffline(true);
      return false;
    }
  };

  win.addEventListener("offline", () => setOffline(true));
  win.addEventListener("online", () => {
    void checkConnection();
  });

  syncRecheckTimer();

  return {
    isOffline: () => isOffline,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    checkConnection,
  };
};

export const connectionStatus = createConnectionStatusStore();
